import { useState, useRef, useEffect, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tab } from "@headlessui/react";

import { useTabStore } from "~/stores";
import { TabKeys } from "~/stores/tab/tab.state";
import { SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

// Layout components
import AppHeader from "~/components/AppHeader";
import ExploreLogo from "~/components/ExploreLogo";
import FilterList from "./Filters/FilterList";

// Result Panel Components
import TextResults from "./Results/Texts/TextResults";
import ImageResults from "./Results/Graphics/ImageResults";
import PagesResults from "./Results/ReportSections/PagesResults";
import FullDocumentResults from "./Results/FullDocuments/FullDocumentResults";

// Modal and viewer-related components
import RIGDialog from "../ReaderIntelligenceGuide/RIGDialog";
import ImageDetailsModal from "./Results/ImageDetailsModal";
import DocumentViewer from "./Results/DocumentViewer";
import useFilters from "~/hooks/useFilters";

const SearchResult: FunctionComponent = () => {
    const { t } = useTranslation();
    const REGION = import.meta.env.VITE_REGION;
    const showRIG = REGION === "us";
    const navigate = useNavigate();

    useFilters();

    const resultsEl = useRef<HTMLDivElement>(null);
    const activeTab = useTabStore(state => state.tab);
    const dispatchTab = useTabStore(state => state.dispatch);
    const tabKeys: TabKeys[] =
        REGION === "us"
            ? [SEARCH_TABS.IMAGES, SEARCH_TABS.KEYWORDS, SEARCH_TABS.REPORTS]
            : [
                  SEARCH_TABS.IMAGES,
                  SEARCH_TABS.KEYWORDS,
                  SEARCH_TABS.REPORTS,
                  SEARCH_TABS.FULL_DOCUMENTS
              ];

    const type = activeTab?.key;
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        setSelectedIndex(activeTab?.id ?? 0);
    }, [activeTab]);

    const handleTabChange = (index: number) => {
        setSelectedIndex(index);
        dispatchTab({ type: SearchActions.SET_ACTIVETAB_BY_KEY, payload: tabKeys[index] });
        navigate("/search");
    };

    return (
        <div className="h-full w-full">
            <Tab.Group
                as="div"
                className="flex flex-col flex-grow z-[5] relative h-full max-h-screen overflow-auto"
                selectedIndex={selectedIndex}
                onChange={handleTabChange}
            >
                <div className="w-full mx-auto shrink-0 flex flex-col items-center text-foreground-dark relative z-30">
                    <div className="w-full h-[60px] bg-white pt-3 px-10 pb-2 flex items-center justify-between">
                        <div className="h-[40px] flex items-center flex-1 justify-start">
                            <ExploreLogo />
                        </div>

                        <Tab.List className="flex flex-1 justify-center">
                            <div className="relative h-full flex items-center space-x-6 z-20">
                                {tabKeys.map(type => (
                                    <Tab
                                        key={type}
                                        className={({ selected }) =>
                                            `py-3 h-full text-sm leading-5 tracking-[-0.28px] outline-none ${
                                                selected
                                                    ? "text-stone-darkest font-bold"
                                                    : "text-stone-vibrant font-medium-light hover:border-b hover:border-stone-primary"
                                            }`
                                        }
                                    >
                                        {t(`searchResultTypes.${type}`)}
                                    </Tab>
                                ))}
                            </div>
                        </Tab.List>

                        <div className="flex flex-1 items-center justify-end">
                            <AppHeader />
                        </div>
                    </div>
                    <FilterList resultType={type} />
                </div>

                <Tab.Panels ref={resultsEl} className="h-full overflow-hidden">
                    <Tab.Panel className="w-full h-full">
                        <ImageResults />
                    </Tab.Panel>

                    <Tab.Panel className="w-full h-full">
                        <TextResults />
                    </Tab.Panel>

                    <Tab.Panel className="w-full h-full">
                        <PagesResults />
                    </Tab.Panel>

                    {REGION === "fr" && (
                        <Tab.Panel className="w-full h-full">
                            <FullDocumentResults />
                        </Tab.Panel>
                    )}
                </Tab.Panels>
            </Tab.Group>

            <ImageDetailsModal />
            <DocumentViewer />
            {showRIG && <RIGDialog />}
        </div>
    );
};

export default SearchResult;
